import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { t } from 'ttag';
import styled from 'styled-components';
import ReactCrop, { Crop } from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';

import { checkeredBackground } from '../../styles';
import Loader, { URL_PROTOCOL_REGEXP } from './Loader';

export type { Crop };

const DEFAULT_CROP_WIDTH = 50;
const DEFAULT_CROP_HEIGHT = 50;

interface Ratio {
  vertical: number;
  horizontal: number;
}

const defaultCrop: Crop = {
  unit: 'px',
  x: 0,
  y: 0,
  width: DEFAULT_CROP_WIDTH,
  height: DEFAULT_CROP_HEIGHT,
  aspect: null,
};

const defaultRatio: Ratio = { vertical: 1, horizontal: 1 };

export const useCrop = (initialCrop: Crop = defaultCrop, initialRatio: Ratio = defaultRatio) => {
  const [crop, setCrop] = useState<Crop>(initialCrop);
  const [ratios, setRatios] = useState<Ratio>(initialRatio);

  useEffect(() => {
    setCrop({
      unit: initialCrop.unit,
      x: initialCrop.x / ratios.horizontal,
      width: initialCrop.width / ratios.horizontal,
      y: initialCrop.y / ratios.vertical,
      height: initialCrop.height / ratios.vertical,
    });
  }, [ratios, initialCrop]);

  return {
    crop,
    setCrop,
    ratios,
    setRatios,
  };
};

export interface Props {
  src: string;
  onCropSaveClick: (result: Crop) => void;
  settings: Crop;
  maxHeight?: number;
  minHeight?: number;
  maxWidth?: number;
  minWidth?: number;
  visible: boolean;
  maskClosable?: boolean;
  onModalClose: () => void;
  circularCrop?: boolean;
}

const StyledReactCrop = styled(ReactCrop)`
  && {
    ${checkeredBackground}
  }
`;

const ImageCrop = ({
  src,
  onCropSaveClick,
  settings,
  maxHeight = 0,
  minHeight = 0,
  maxWidth = 0,
  minWidth = 0,
  visible,
  onModalClose,
  maskClosable = false,
  circularCrop = false,
}: Props) => {
  // The initial state for cropImageUrl is null for showing the Loading Spin until the image
  // is fully loaded. Once the cropImageUrl is set, we have to observe 'src' - once it's changed
  // we have to show the spin again until the new image is also loaded.
  const [url, setUrl] = useState(null);
  useEffect(() => {
    setUrl(null);
  }, [src]);

  const { crop, setCrop, ratios, setRatios } = useCrop(settings);

  const onOk = () => {
    const cropPayload = {
      ...crop,
      x: crop.x * ratios.horizontal,
      width: crop.width * ratios.horizontal,
      y: crop.y * ratios.vertical,
      height: crop.height * ratios.vertical,
    };

    if (onCropSaveClick) {
      onCropSaveClick(cropPayload);
    }
  };

  const onImageLoaded = (image: HTMLImageElement) => {
    const { height, width, naturalHeight, naturalWidth } = image;

    setRatios({
      vertical: naturalHeight / height,
      horizontal: naturalWidth / width,
    });
  };

  return (
    <Modal
      title={t`Crop Image`}
      centered
      closable={false}
      maskClosable={maskClosable}
      open={visible}
      onCancel={onModalClose}
      onOk={onOk}
      okText={t`Crop & Save Image`}
      okButtonProps={{ disabled: !url }}
      cancelText={t`Close`}
    >
      {url ? (
        <StyledReactCrop
          src={src}
          crop={crop}
          circularCrop={circularCrop}
          maxHeight={maxHeight ? maxHeight / ratios.vertical : undefined}
          minHeight={minHeight ? minHeight / ratios.vertical : undefined}
          maxWidth={maxWidth ? maxWidth / ratios.horizontal : undefined}
          minWidth={minWidth ? minWidth / ratios.horizontal : undefined}
          onChange={next => {
            // Force aspect ratio to 1 for circular crops
            if (circularCrop) {
              const size = Math.min(next.width, next.height);
              setCrop({
                ...next,
                x: Math.max(0, next.x),
                y: Math.max(0, next.y),
                width: size,
                height: size,
              });
            } else {
              setCrop({
                ...next,
                // due to a bug in react-image-crop library, we receive negative values when
                // cropping is started from the top-left corner.
                // this may be a related bug
                // https://github.com/DominicTobias/react-image-crop/issues/377
                x: Math.max(0, next.x),
                y: Math.max(0, next.y),
              });
            }
          }}
          onImageLoaded={onImageLoaded}
        />
      ) : (
        <Loader src={src} onLoad={() => setUrl(src)} />
      )}
    </Modal>
  );
};

ImageCrop.propTypes = {
  src: {
    isRequired: (props: Record<string, unknown>, propName: string, componentName: string) => {
      if (!URL_PROTOCOL_REGEXP.test(props[propName] as string)) {
        return new Error(
          `Invalid prop \`${propName}\` supplied to \`${componentName}\`. Validation failed.`,
        );
      }

      return null;
    },
  }.isRequired,
};

export default ImageCrop;
